import React from 'react';
import { noop } from 'lodash';

import { RunIdentificationPair } from '@neptune/shared/entity-domain';

type CompareContext = {
  /**
   * Number of all items selected to compare that are visible in charts.
   * This represents the actual count of runs being displayed in comparison views.
   */
  count: number;
  /** Number of selected items that are hidden due to applied filters */
  hiddenCount: number;
  /** Total number of items selected for comparison, regardless of any applied filters */
  selectedCount: number;
  /** Number of selected runs matching filters */
  filteredRunIdsCount: number;
  /** Number of selected experiments matching filters */
  filteredExperimentNamesCount: number;
  /** Number of added runs matching filters */
  filteredAddedRunIdsCount: number;
  /** Number of added experiments matching filters */
  filteredAddedExperimentNamesCount: number;
  /** Array of sys/id that are selected to compare */
  runIds: string[];
  /** Limit of items that can be selected automatically */
  autoCompareLimit?: number;
  /** Flag that indicates if user can modify items selected compare */
  canEditCompare: boolean;
  /** sys/name and run/id array selected to compare. sys/name has higher precedence it will be present in this array instead of sys/id */
  knownIdsAndNames: string[];
  /** sys/name and run/id array selected to compare after applying filters */
  filteredKnownIdsAndNames: string[];
  /** Map of run id to experiment name, useful to determine if something was selected as experiment or run */
  runIdToExperimentName: Record<string, string>;
  /** function that indicates if the item is selected as experiment */
  isSelectedAsExperiment: (experimentName?: string) => boolean;
  /** function that indicates if the item is selected as run */
  isSelectedAsRun: (runId?: string) => boolean;
  /** function that indicates if the item is currently included in compare membership */
  isAdded: (key?: string) => boolean;
  /** Rename to showOnlyAddedRuns when we implement it for every feature (ref: getTristateSelectionEnabled)  */
  showOnlySelectedRuns?: boolean;
  showSelectedHiddenByFilter?: boolean;
  runIdsReady?: boolean;
  clearCompare: () => void;
  removeAllFromCompare: () => void;
  /** map of qualified names containing sys/id to number representing the order of the item in the comparison */
  runsOrderMap: Map<string, number>;
  autoModeChange: (n?: number) => void;
  showOnlySelectedRunsChange?: (value: boolean) => void;
  showSelectedHiddenByFilterChange?: (value: boolean) => void;
  addToCompare: (runIdentificationPairs: RunIdentificationPair[]) => void;
  removeFromCompare: ({
    identificationPairsToRemove,
    allowedToRemovePair,
  }: {
    identificationPairsToRemove: RunIdentificationPair[];
    allowedToRemovePair?: boolean;
  }) => void;
  hideFromCompare: ({
    identificationPairsToHide,
    allowedToRemovePair,
  }: {
    identificationPairsToHide: RunIdentificationPair[];
    allowedToRemovePair?: boolean;
  }) => void;

  /**
   * List of selected experiment names.
   * This is a subset of knownIdsAndNames, and is used to determine if something was selected as an experiment or run.
   * It is not used to determine if something is selected to compare (further async filtering may be applied).
   */
  selectedExperimentNames: string[];
  /**
   * List of selected run ids.
   * This is a subset of knownIdsAndNames, and is used to determine if something was selected as an experiment or run.
   * It is not used to determine if something is selected to compare (further async filtering may be applied).
   */
  selectedRunIds: string[];
  /** List of experiment names included in compare membership */
  addedExperimentNames: string[];
  /** List of run ids included in compare membership */
  addedRunIds: string[];
  /** TODO: remove when we get rid of getTristateSelectionEnabled */
  isTristateEnabled?: boolean;
};

export const CompareContext = React.createContext<CompareContext>({
  count: 0,
  hiddenCount: 0,
  selectedCount: 0,
  filteredRunIdsCount: 0,
  filteredExperimentNamesCount: 0,
  filteredAddedRunIdsCount: 0,
  filteredAddedExperimentNamesCount: 0,
  runIds: [],
  autoCompareLimit: undefined,
  knownIdsAndNames: [],
  filteredKnownIdsAndNames: [],
  runIdToExperimentName: {},
  isSelectedAsExperiment: () => false,
  isSelectedAsRun: () => false,
  isAdded: () => false,
  clearCompare: noop,
  removeAllFromCompare: noop,
  addToCompare: noop,
  autoModeChange: noop,
  removeFromCompare: noop,
  hideFromCompare: noop,
  canEditCompare: false,
  showSelectedHiddenByFilter: false,
  showSelectedHiddenByFilterChange: noop,
  showOnlySelectedRuns: false,
  showOnlySelectedRunsChange: noop,
  runsOrderMap: new Map(),
  selectedExperimentNames: [],
  selectedRunIds: [],
  addedExperimentNames: [],
  addedRunIds: [],
});
